import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  skipToken,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export type UseSearchResourcePagesQueryOptions<T> = UseQueryOptions<
  T[],
  Error,
  T[],
  ReturnType<(typeof cacheKeys)['searchResourcePages']>
>

interface UseSearchResourcePagesOptions<T> {
  query?: Omit<
    UseSearchResourcePagesQueryOptions<T>,
    'initialData' | 'queryKey' | 'queryFn' | 'keepPreviousData'
  >
}

export function useSearchResourcePages<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  query?: QueryTypes,
  options?: UseSearchResourcePagesOptions<T> | null
): UseQueryResult<T[]> {
  const medplum = useMedplum()

  const parameters = normalizeQuery(query)

  const searchKey = query
    ? getSearchKey(medplum, resourceClass.RESOURCE_TYPE, parameters)
    : NO_QUERY

  const result = useQuery({
    ...options?.query,
    queryKey: cacheKeys.searchResourcePages(
      resourceClass.RESOURCE_TYPE,
      searchKey
    ),
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : async () => {
            /* Keyed by id rather than pushed into an array: a query whose
             * `_sort` is not unique orders tied resources differently per
             * request, so paging by offset can return the same resource on two
             * pages. Callers treat this list as a set of distinct resources —
             * MiniSearch's `addAll` throws outright on a repeated id — so a
             * duplicate here surfaces as a broken feature far from its cause.
             * The first copy wins; both are the same resource. */
            const byId = new Map<string, T>()
            for await (const page of medplum.searchResourceClassPages(
              resourceClass,
              parameters
            )) {
              for (const resource of page) {
                if (!byId.has(resource.id)) {
                  byId.set(resource.id, resource)
                }
              }
            }
            return [...byId.values()]
          },
    meta: {
      ...options?.query?.meta,
      queryParams: parameters,
      resourceClass,
    },
  })

  useErrorToast(
    result.error &&
      // Ignore signal aborted errors as they're expected during cancellation
      !(result.error instanceof Error && result.error.name === 'AbortError') &&
      `Error accessing ${getSearchKey(medplum, resourceClass.RESOURCE_TYPE, normalizeQuery(query))}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
